import React, { Suspense, lazy, useEffect } from "react";
import { useCartSync } from "@/hooks/useCartSync";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AuthProvider } from "@/contexts/AuthContext";
import { AuthGuard } from "@/components/AuthGuard";
import { RoleRedirect } from "@/components/RoleRedirect";
import { ThemeProvider } from "@/components/ThemeProvider";
import { HelmetProvider } from "react-helmet-async";


// Eager load only the landing page for fast initial render
import Index from "./pages/Index";

// Lazy load all other routes to reduce initial bundle size
const ExamPage = lazy(() => import("./pages/ExamPage"));
const ExamPapersPage = lazy(() => import("./pages/ExamPapersPage"));
const StudyPlannerPage = lazy(() => import("./pages/StudyPlannerPage"));
const ExamCalendarPage = lazy(() => import("./pages/ExamCalendarPage"));
const ResultsPage = lazy(() => import("./pages/ResultsPage"));
const PremiumPage = lazy(() => import("./pages/PremiumPage"));
const PremiumSuccessPage = lazy(() => import("./pages/PremiumSuccessPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const AdminUploadPage = lazy(() => import("./pages/AdminUploadPage"));

const AdminQuestionUploadPage = lazy(() => import("./pages/AdminQuestionUploadPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const MathHubAIPage = lazy(() => import("./pages/MathHubAIPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const StudyMaterialsPage = lazy(() => import("./pages/StudyMaterialsPage"));
const PracticeSetsPage = lazy(() => import("./pages/PracticeSetsPage"));
const VideoTutorialsPage = lazy(() => import("./pages/VideoTutorialsPage"));
const MentorshipsPage = lazy(() => import("./pages/MentorshipsPage"));
const MentorApplicationPage = lazy(() => import("./pages/MentorApplicationPage"));
const MentorProfilePage = lazy(() => import("./pages/MentorProfilePage"));
const MentorDashboardPage = lazy(() => import("./pages/MentorDashboardPage"));
const AdminMentorVerificationPage = lazy(() => import("./pages/AdminMentorVerificationPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const BlogsPage = lazy(() => import("./pages/BlogsPage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));
const RoleSelection = lazy(() => import("./pages/RoleSelection"));
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboard"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const CreateClassroom = lazy(() => import("./pages/CreateClassroom"));
const ClassroomDetail = lazy(() => import("./pages/ClassroomDetail"));
const JoinClassroom = lazy(() => import("./pages/JoinClassroom"));
const StudentClassroomDetail = lazy(() => import("./pages/StudentClassroomDetail"));
const TeacherExamCreation = lazy(() => import("./pages/TeacherExamCreation"));
const TeacherExamManage = lazy(() => import("./pages/TeacherExamManage"));
const DetailedResultsPage = lazy(() => import("./pages/DetailedResultsPage"));
const TeacherProctoringReports = lazy(() => import("./pages/TeacherProctoringReports"));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));

const queryClient = new QueryClient();

const AppContent = () => {
  useCartSync();
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        navigate('/reset-password');
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/shop" element={<MarketplacePage />} />
        <Route path="/product/:handle" element={<ProductDetailPage />} />
        <Route path="/exams/:examId" element={<ExamPage />} />
        <Route path="/exam-papers" element={<ExamPapersPage />} />
        <Route path="/study-planner" element={<StudyPlannerPage />} />
        <Route path="/exam-calendar" element={
          <AuthGuard>
            <ExamCalendarPage />
          </AuthGuard>
        } />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/resources/study-materials" element={<StudyMaterialsPage />} />
        <Route path="/resources/practice-sets" element={<PracticeSetsPage />} />
        <Route path="/resources/video-tutorials" element={<VideoTutorialsPage />} />
        <Route path="/mentorships" element={<MentorshipsPage />} />
        <Route path="/mentor/:id" element={<MentorProfilePage />} />
        <Route path="/mentor-application" element={
          <AuthGuard>
            <MentorApplicationPage />
          </AuthGuard>
        } />
        <Route path="/mentor-dashboard" element={
          <AuthGuard>
            <MentorDashboardPage />
          </AuthGuard>
        } />
        <Route path="/results" element={
          <AuthGuard>
            <ResultsPage />
          </AuthGuard>
        } />
        <Route path="/results/:resultId" element={
          <AuthGuard>
            <DetailedResultsPage />
          </AuthGuard>
        } />
        <Route path="/premium" element={
          <AuthGuard>
            <PremiumPage />
          </AuthGuard>
        } />
        <Route path="/premium-success" element={
          <AuthGuard>
            <PremiumSuccessPage />
          </AuthGuard>
        } />
        <Route path="/profile" element={
          <AuthGuard>
            <ProfilePage />
          </AuthGuard>
        } />
        <Route path="/support" element={
          <AuthGuard>
            <SupportPage />
          </AuthGuard>
        } />
        <Route path="/mathhub-ai" element={
          <AuthGuard>
            <MathHubAIPage />
          </AuthGuard>
        } />
        <Route path="/admin/upload" element={
          <AuthGuard requireAdmin>
            <AdminUploadPage />
          </AuthGuard>
        } />
        <Route path="/admin/questions/:examId" element={
          <AuthGuard requireAdmin>
            <AdminQuestionUploadPage />
          </AuthGuard>
        } />
        <Route path="/admin/mentor-verification" element={
          <AuthGuard requireAdmin>
            <AdminMentorVerificationPage />
          </AuthGuard>
        } />
        <Route path="/blogs" element={<BlogsPage />} />
        <Route path="/blogs/:id" element={<BlogDetailPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/role-selection" element={
          <AuthGuard>
            <RoleSelection />
          </AuthGuard>
        } />
        <Route path="/teacher/dashboard" element={
          <AuthGuard>
            <RoleRedirect>
              <TeacherDashboard />
            </RoleRedirect>
          </AuthGuard>
        } />
        <Route path="/teacher/classroom/new" element={
          <AuthGuard>
            <RoleRedirect>
              <CreateClassroom />
            </RoleRedirect>
          </AuthGuard>
        } />
        <Route path="/teacher/classroom/:id" element={
          <AuthGuard>
            <RoleRedirect>
              <ClassroomDetail />
            </RoleRedirect>
          </AuthGuard>
        } />
        <Route path="/teacher/exam/new" element={
          <AuthGuard>
            <RoleRedirect>
              <TeacherExamCreation />
            </RoleRedirect>
          </AuthGuard>
        } />
        <Route path="/teacher/exam/:examId/manage" element={
          <AuthGuard>
            <RoleRedirect>
              <TeacherExamManage />
            </RoleRedirect>
          </AuthGuard>
        } />
        <Route path="/teacher/proctoring-reports" element={
          <AuthGuard>
            <RoleRedirect>
              <TeacherProctoringReports />
            </RoleRedirect>
          </AuthGuard>
        } />
        <Route path="/student/dashboard" element={
          <AuthGuard>
            <RoleRedirect>
              <StudentDashboard />
            </RoleRedirect>
          </AuthGuard>
        } />
        <Route path="/student/join-classroom" element={
          <AuthGuard>
            <RoleRedirect>
              <JoinClassroom />
            </RoleRedirect>
          </AuthGuard>
        } />
        <Route path="/student/classroom/:id" element={
          <AuthGuard>
            <RoleRedirect>
              <StudentClassroomDetail />
            </RoleRedirect>
          </AuthGuard>
        } />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <HelmetProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </HelmetProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);

export default App;
